import { useMemo } from "react";
import { toast } from "sonner";
import { X, Printer, Link as LinkIcon, ExternalLink, ScanLine, Check } from "lucide-react";
import QRCode from "./QRCode";
import { BrandMark } from "./BrandMark";

/**
 * One-tap printable QR poster for a program + a copy-join-link button.
 *
 * - The poster lives inside a `.print-poster` wrapper. index.css has a
 *   `@media print` rule that hides everything else on the page so hitting
 *   Print (or Save as PDF) produces a clean, standalone poster.
 * - Copies the join link to clipboard on tap.
 * - Also renders a preview inside a modal so the owner can see what will print.
 */
export function PosterModal({ open, program, orgName, orgSlug, onClose, joinUrl, size = "a5" }) {
  const url = useMemo(
    () => joinUrl || (program && orgSlug
      // window.location.origin (not REACT_APP_BACKEND_URL) so the QR always
      // encodes whatever domain the owner is on — when they deploy to their
      // real domain, printed posters carry their brand automatically.
      ? `${window.location.origin}/api/j/${orgSlug}/${program.slug}?src=poster`
      : ""),
    [joinUrl, program, orgSlug]
  );
  if (!open || !program) return null;

  // Layout tokens by size variant. A5 = counter, A4 = wall, tent = 3-up.
  const SIZES = {
    a5:   { width: 560, height: 794, qr: 280, headerFont: 44, tag: "A5" },
    a4:   { width: 794, height: 1123, qr: 400, headerFont: 60, tag: "A4" },
    tent: { width: 560, height: 794, qr: 200, headerFont: 32, tag: "Tent (3-up)" },
  };
  const S = SIZES[size] || SIZES.a5;

  const reward = program.reward || {};
  const rewardLine = reward.name && reward.threshold
    ? `${reward.name} after ${reward.threshold} ${program.type === "points" ? "points" : program.type === "visit" ? "visits" : "stamps"}`
    : reward.name || "Rewards you can taste";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Join link copied");
    } catch (_) { toast.error("Copy failed — long-press the link and copy it manually."); }
  };

  const doPrint = () => {
    // Small delay ensures the printer preview picks up final layout.
    setTimeout(() => window.print(), 30);
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0B0D10]/60 backdrop-blur-sm flex flex-col" data-testid="poster-modal" onClick={onClose}>
      {/* Screen-only toolbar (hidden when printing) */}
      <div className="print:hidden shrink-0 flex items-center justify-between px-4 sm:px-8 py-4 text-white">
        <div className="flex items-center gap-2 text-[13px]"><ScanLine size={15} className="text-[#C8FF4D]" />Poster preview</div>
        <div className="flex items-center gap-2">
          <button onClick={(e) => { e.stopPropagation(); copyLink(); }} data-testid="poster-copy-link"
            className="h-9 px-3.5 rounded-full bg-white/[0.09] hover:bg-white/[0.16] border border-white/10 text-white text-[12.5px] font-medium inline-flex items-center gap-1.5">
            <LinkIcon size={13} />Copy link
          </button>
          <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid="poster-open-link"
            className="h-9 px-3.5 rounded-full bg-white/[0.09] hover:bg-white/[0.16] border border-white/10 text-white text-[12.5px] font-medium inline-flex items-center gap-1.5">
            <ExternalLink size={13} />Open
          </a>
          <button onClick={(e) => { e.stopPropagation(); doPrint(); }} data-testid="poster-print"
            className="h-9 px-4 rounded-full bg-[#C8FF4D] hover:bg-[#b8ef3d] text-[#0B0D10] text-[12.5px] font-bold inline-flex items-center gap-1.5">
            <Printer size={13} />Print · Save PDF
          </button>
          <button onClick={onClose} data-testid="poster-close"
            className="h-9 w-9 rounded-full bg-white/[0.09] hover:bg-white/[0.16] border border-white/10 grid place-items-center text-white ml-1"><X size={14} /></button>
        </div>
      </div>

      <div className="flex-1 overflow-auto flex items-start justify-center p-4 sm:p-8" onClick={(e) => e.stopPropagation()}>
        {/*
         * Poster: A5 portrait aspect (148:210). Fixed pixel dimensions so print
         * output is deterministic — @media print scales to a real A5 sheet.
         * On screen we scale it down via transform to fit the viewport.
         */}
        <div className="print-poster bg-white text-[#0B0D10] shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)] print:shadow-none"
          style={{ width: `${S.width}px`, height: `${S.height}px`, position: "relative", flexShrink: 0 }}
          data-testid="poster-sheet"
          data-size={size}>

          {/* Header slab */}
          <div className="bg-[#0B0D10] text-white px-9 pt-9 pb-8 relative overflow-hidden" style={{ height: "232px" }}>
            <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-[#C8FF4D]/16 blur-3xl print:hidden" />
            <div className="relative z-10 flex items-start justify-between">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#C8FF4D] font-bold">Join our loyalty club</div>
                <h1 className="font-display font-extrabold tracking-[-0.02em] mt-3 leading-[0.92]" style={{ fontSize: `${S.headerFont}px` }}>
                  {orgName || "Our shop"}
                </h1>
                <div className="text-white/70 mt-3 text-[16px] font-medium leading-snug max-w-[300px]">{rewardLine}</div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <div className="h-9 w-9"><BrandMark size={36} /></div>
                <div className="text-[9.5px] uppercase tracking-[0.18em] text-white/40 text-right leading-tight">Powered by<br />BackPocket</div>
              </div>
            </div>
          </div>

          {/* QR block */}
          <div className="px-9 pt-8 pb-4 flex flex-col items-center">
            <div className="rounded-[22px] bg-white border-[3px] border-[#0B0D10] p-5 shadow-[0_16px_40px_-24px_rgba(11,13,16,0.35)] print:shadow-none">
              <QRCode value={url} size={S.qr} />
            </div>
            <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-[#0B0D10]/50 font-mono">{program.slug}</div>
          </div>

          {/* Instructions */}
          <div className="px-9 pt-3 pb-6">
            <div className="grid grid-cols-3 gap-3">
              {[
                { n: 1, t: "Point", d: "your phone camera at the code" },
                { n: 2, t: "Tap", d: "the link that pops up" },
                { n: 3, t: "Add", d: "the pass to your wallet" },
              ].map((s) => (
                <div key={s.n} className="text-center">
                  <div className="mx-auto h-8 w-8 rounded-full bg-[#0B0D10] text-[#C8FF4D] font-display font-extrabold grid place-items-center text-[15px]">{s.n}</div>
                  <div className="mt-2 font-display font-bold text-[15px]">{s.t}</div>
                  <div className="text-[11px] text-[#0B0D10]/60 leading-tight mt-0.5">{s.d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer band */}
          <div className="absolute bottom-0 left-0 right-0 bg-[#F4F1E9] border-t border-[#E8E5DE] px-9 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-[11.5px] text-[#0B0D10]/70">
                <span className="inline-flex items-center gap-1"><Check size={12} strokeWidth={3} className="text-[#0B0D10]" />No app</span>
                <span className="text-[#0B0D10]/30">·</span>
                <span className="inline-flex items-center gap-1"><Check size={12} strokeWidth={3} className="text-[#0B0D10]" />No signup</span>
                <span className="text-[#0B0D10]/30">·</span>
                <span className="inline-flex items-center gap-1"><Check size={12} strokeWidth={3} className="text-[#0B0D10]" />Under a minute</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#0B0D10]/45 font-mono truncate max-w-[210px]">{url.replace(/^https?:\/\//, "")}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="print:hidden shrink-0 px-8 pb-6 text-center text-white/45 text-[11.5px]">
        {size === "tent"
          ? "Prints as a 3-up table-tent on A4. Cut, fold, place on tables."
          : size === "a4"
          ? "Prints on A4. Ideal for shop windows and community boards."
          : "Prints on A5 by default. Hit Print then choose Save as PDF."}
      </div>
    </div>
  );
}
